// MindForge web — TypeScript port of logic/sequences.py.
//
// Invariants the Python tests assert (must hold here too):
//   - generateDigitSequence: no two consecutive equal digits
//   - generateLetterSequence: all unique, uppercase, capped at 26
//   - FairDeck: every item appears once per cycle before any item repeats
//
// We use Math.random() directly. The Python reference uses random.shuffle /
// random.sample, but the cross-language contract is on invariants, not on
// reproducing specific sequences — different PRNGs are fine.

const ASCII_UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/** In-place Fisher–Yates shuffle, matching random.shuffle's mutation semantics. */
function shuffleInPlace<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

/** Equivalent to Python's random.sample(seq, n) — n unique items in random order. */
function sample<T>(pool: readonly T[], n: number): T[] {
  if (n <= 0) return [];
  const k = Math.min(n, pool.length);
  const copy = [...pool];
  shuffleInPlace(copy);
  return copy.slice(0, k);
}

/** Integer in [0, max) — matches Python's random.randrange(max). */
function randrange(max: number): number {
  return Math.floor(Math.random() * max);
}

/**
 * Cyclic shuffled pool — every item appears once per cycle before any repeats.
 * Eliminates the clustering pure random.choice produces over long sessions.
 */
export class FairDeck<T> {
  private readonly pool: readonly T[];
  private deck: T[];

  constructor(items: Iterable<T>) {
    this.pool = Array.from(items);
    this.deck = [];
  }

  draw(): T {
    if (this.deck.length === 0) {
      this.deck = [...this.pool];
      shuffleInPlace(this.deck);
    }
    // Python uses .pop() (last element). Match for parity.
    return this.deck.pop() as T;
  }

  /** Return an item to a random position in the remaining deck. */
  putBack(item: T): void {
    const pos = randrange(this.deck.length + 1);
    this.deck.splice(pos, 0, item);
  }
}

/** Generate a digit sequence with no two consecutive equal digits. */
export function generateDigitSequence(length: number): number[] {
  if (length <= 0) return [];
  const deck = new FairDeck<number>([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]);
  const seq: number[] = [];
  while (seq.length < length) {
    const d = deck.draw();
    if (seq.length > 0 && d === seq[seq.length - 1]) {
      // Repeat — put it back at a random slot, try again next loop.
      deck.putBack(d);
    } else {
      seq.push(d);
    }
  }
  return seq;
}

/** Generate a unique letter sequence (no repeats, uppercase, capped at 26). */
export function generateLetterSequence(length: number): string[] {
  if (length <= 0) return [];
  const n = Math.min(length, 26);
  return sample(ASCII_UPPERCASE.split(""), n);
}

/** Generate a sequence of single characters using a FairDeck. */
export function generateMixedSequence(
  length: number,
  useDigits = true,
): string[] {
  if (length <= 0) return [];
  const pool: string[] = useDigits
    ? ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]
    : ASCII_UPPERCASE.split("");
  const deck = new FairDeck<string>(pool);
  const out: string[] = [];
  for (let i = 0; i < length; i++) out.push(deck.draw());
  return out;
}

/** Split a sequence into chunks of the given size. */
export function chunkSequence<T>(sequence: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) return [];
  const chunks: T[][] = [];
  for (let i = 0; i < sequence.length; i += chunkSize) {
    chunks.push(sequence.slice(i, i + chunkSize));
  }
  return chunks;
}

/** Whether the player's input exactly matches the expected sequence. */
export function checkExactMatch<T>(playerInput: T[], expected: T[]): boolean {
  if (playerInput.length !== expected.length) return false;
  for (let i = 0; i < expected.length; i++) {
    if (playerInput[i] !== expected[i]) return false;
  }
  return true;
}

/** Count items in correct positions (partial credit). */
export function countCorrectPositions<T>(
  playerInput: T[],
  expected: T[],
): number {
  const n = Math.min(playerInput.length, expected.length);
  let correct = 0;
  for (let i = 0; i < n; i++) {
    if (playerInput[i] === expected[i]) correct += 1;
  }
  return correct;
}

/** Fraction of items in correct positions, or 0.0 if expected is empty. */
export function partialAccuracy<T>(
  playerInput: T[],
  expected: T[],
): number {
  if (expected.length === 0) return 0.0;
  return countCorrectPositions(playerInput, expected) / expected.length;
}
